import * as THREE from "three";
import { GLTFLoader, type GLTF } from "three/addons/loaders/GLTFLoader.js";
import { clone as cloneSkeleton } from "three/addons/utils/SkeletonUtils.js";
import { modelUrl } from "./manifest.js";
import { batchStaticMeshes } from "./batch.js";

export interface LoadedModel {
  /** Template scene; call `instantiate()` for a scene-ready clone with its own mixer. */
  template: THREE.Group;
  clips: THREE.AnimationClip[];
  url: string;
}

export interface ModelInstance {
  object: THREE.Group;
  mixer: THREE.AnimationMixer;
  actions: Record<string, THREE.AnimationAction>;
  play(clip: string, options?: { loop?: boolean; fade?: number; timeScale?: number }): THREE.AnimationAction | null;
}

const loader = new GLTFLoader();
const cache = new Map<string, Promise<LoadedModel>>();
let pendingModels = 0;

/**
 * The pipeline bakes the same texture atlases into every model of a family: one building file carries 13 MB of
 * `buildings-color/-normal/-orm`, and a base with twenty buildings would upload those same atlases twenty times.
 * Phones run out of graphics memory doing that and the browser drops the context. Named atlases are therefore
 * shared across models: the first file that brings one keeps it, every later file points at the same texture.
 */
const atlases = new Map<string, THREE.Texture>();
const MAP_SLOTS = ["map", "normalMap", "roughnessMap", "metalnessMap", "aoMap", "emissiveMap"] as const;

function atlasKey(texture: THREE.Texture): string | null {
  const name = texture.name;
  if (!name) return null;
  const image = texture.image as { width?: number; height?: number } | null;
  return `${name}:${image?.width ?? 0}x${image?.height ?? 0}:${texture.colorSpace}:${texture.flipY ? 1 : 0}`;
}

function shareAtlases(scene: THREE.Object3D): void {
  scene.traverse((object) => {
    const mesh = object as THREE.Mesh;
    if (!mesh.isMesh) return;
    for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
      const std = material as THREE.MeshStandardMaterial & Record<string, unknown>;
      for (const slot of MAP_SLOTS) {
        const texture = std[slot] as THREE.Texture | null | undefined;
        if (!texture) continue;
        const key = atlasKey(texture);
        if (!key) continue;
        const shared = atlases.get(key);
        if (!shared) {
          atlases.set(key, texture);
          continue;
        }
        if (shared === texture) continue;
        std[slot] = shared;
        texture.dispose();
      }
    }
  });
}

/** Includes image decoding and preparation, not merely completed HTTP responses. */
export function pendingModelCount(): number { return pendingModels; }

function prepare(gltf: GLTF): void {
  gltf.scene.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh) {
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const m of mats) {
        const std = m as THREE.MeshStandardMaterial;
        if (std.emissive && std.emissiveIntensity > 1) std.toneMapped = true;
      }
    }
  });
  shareAtlases(gltf.scene);
  gltf.scene.userData["batchStats"] = batchStaticMeshes(gltf.scene, gltf.animations);
}

/** Loads (and caches) a glTF by asset id; buildings need the tier, zombies the variant. */
export function loadModel(id: string, variant?: number | string, lod: 0 | 1 = 0): Promise<LoadedModel> {
  const url = modelUrl(id, variant, lod);
  let p = cache.get(url);
  if (!p) {
    pendingModels++;
    p = new Promise<LoadedModel>((resolve, reject) => {
      loader.load(
        url,
        (gltf) => {
          try {
            prepare(gltf);
            resolve({ template: gltf.scene, clips: gltf.animations, url });
          } catch (error) {
            reject(error);
          }
        },
        undefined,
        (err) => reject(err instanceof Error ? err : new Error(String(err))),
      );
    }).finally(() => { pendingModels--; });
    cache.set(url, p);
    void p.catch(() => {
      if (cache.get(url) === p) cache.delete(url);
    });
  }
  return p;
}

/** Deep-clones the template and wires an AnimationMixer with one action per clip. */
export function instantiate(model: LoadedModel): ModelInstance {
  const object = cloneSkeleton(model.template) as THREE.Group;
  const mixer = new THREE.AnimationMixer(object);
  const actions: Record<string, THREE.AnimationAction> = {};
  for (const clip of model.clips) actions[clip.name] = mixer.clipAction(clip);
  let current: THREE.AnimationAction | null = null;
  return {
    object,
    mixer,
    actions,
    play(clip, options = {}) {
      const action = actions[clip];
      if (!action) return null;
      const loop = options.loop ?? (clip === "idle" || clip === "walk");
      action.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);
      action.clampWhenFinished = !loop;
      action.timeScale = options.timeScale ?? 1;
      action.reset();
      if (current && current !== action) {
        current.crossFadeTo(action, options.fade ?? 0.15, false);
      }
      action.play();
      current = action;
      return action;
    },
  };
}

export function clearModelCache(): void {
  // The shared atlases belong to the templates that are being dropped (and to the graphics context that may
  // have died with them), so the next load starts from a clean slate.
  atlases.clear();
  cache.clear();
}
